import { BarChart, TableIcon } from 'components/icons';
import PageHeader from 'components/PageHeader';
import { NewDesignIsland } from 'components/ui/overlay-motion';
import PaddedLayout from 'layout/MainLayout/PaddedLayout';
import { FilterPills } from 'pages/home/widgets/share/FilterPills';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { FeatureFlag } from 'types/features';
import { t } from 'utils/getTranslations';
import {
    categoryPath,
    isReportCategory,
    REPORT_CATEGORIES,
    ReportCategory,
    ReportEntry,
    reportPath,
    REPORTS,
} from './reportCatalog';
import { ReportTags } from './ReportTags';

const ALL = 'all';

function ReportCard({ report }: { report: ReportEntry }) {
    const Icon = report.view === 'chart' ? BarChart : TableIcon;
    return (
        <Link
            to={reportPath(report)}
            className="bg-card ring-foreground/10 text-foreground hover:ring-primary/40 focus-visible:ring-ring flex min-h-28 flex-col gap-2 rounded-xl p-4 no-underline ring-1 transition-shadow hover:shadow-xs"
        >
            <span className="flex items-center gap-2.5">
                <span className="bg-accent text-primary flex size-7 shrink-0 items-center justify-center rounded-md">
                    <Icon className="size-4" />
                </span>
                <span className="text-sm font-semibold">{t(`reporting.${report.key}.title`)}</span>
            </span>
            <span className="text-foreground-secondary text-xs leading-[18px]">
                {t(`reporting.${report.key}.description`)}
            </span>
            <ReportTags report={report} className="mt-auto" />
        </Link>
    );
}

function CategorySection({ category, reports }: { category: ReportCategory; reports: ReportEntry[] }) {
    return (
        <section className="flex flex-col gap-3" aria-labelledby={`reports-${category}`}>
            <h2 id={`reports-${category}`} className="m-0 text-base font-semibold">
                {t(`reporting.category.${category}`)}
            </h2>
            <div className="new:gap-(--card-gap) grid grid-cols-[repeat(auto-fill,minmax(270px,1fr))] gap-3">
                {reports.map((report) => (
                    <ReportCard key={report.key} report={report} />
                ))}
            </div>
        </section>
    );
}

/**
 * `/reports` lists every report the user can open, by category; `?category=` narrows it to one. A report sits in
 * one category, but which one is not always plain, so the page shows them all unless asked. `/reports/:category`
 * is the older link to one category and lands on the same view.
 */
export function ReportsHubPage() {
    const { category: legacyCategory } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const enabledFlags = {
        [FeatureFlag.REPORT_WORKING_TIME_BY_EMPLOYEE]: useIsFeatureEnabled(FeatureFlag.REPORT_WORKING_TIME_BY_EMPLOYEE),
    } as Partial<Record<FeatureFlag, boolean>>;
    if (legacyCategory !== undefined) {
        return <Navigate to={isReportCategory(legacyCategory) ? categoryPath(legacyCategory) : '/reports'} replace />;
    }
    const visible = REPORTS.filter((report) => !report.featureFlag || enabledFlags[report.featureFlag]);
    const categories = REPORT_CATEGORIES.filter((each) => visible.some((report) => report.category === each));
    const picked = searchParams.get('category');
    const category = isReportCategory(picked) && categories.includes(picked) ? picked : ALL;
    const shown = category === ALL ? categories : [category];
    return (
        <PaddedLayout>
            <div className="flex flex-col gap-6">
                <PageHeader titleId="reporting.title" showBreadcrumb />
                <NewDesignIsland>
                    <FilterPills
                        label={t('reporting.category-filter')}
                        value={category}
                        options={[
                            { value: ALL, label: t('reporting.all-categories') },
                            ...categories.map((each) => ({ value: each, label: t(`reporting.category.${each}`) })),
                        ]}
                        onChange={(value) =>
                            setSearchParams(value === ALL ? {} : { category: value }, { replace: true })
                        }
                    />
                </NewDesignIsland>
                {shown.map((each) => (
                    <CategorySection
                        key={each}
                        category={each}
                        reports={visible.filter((report) => report.category === each)}
                    />
                ))}
            </div>
        </PaddedLayout>
    );
}
